/**
 * The tracker rail — the delivery-app device, translated into the portal's
 * language: hairline rule, ink fill, monospace labels. No gradients, no glow.
 * The current node breathes; everything else stays still.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { SETUP_STAGES, STAGE_DEFS, stageIndex, type SetupStage } from '../../lib/setupFlow';
import { cn } from '@/lib/utils';

function CheckMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
    </svg>
  );
}

export default function StageTracker({ stage }: { stage: SetupStage }) {
  const current = stageIndex(stage);
  const lastIndex = SETUP_STAGES.length - 1;
  const fill = current / lastIndex;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white px-5 py-6 sm:px-8 sm:py-7">
      <div className="relative">
        {/* Rail */}
        <div className="absolute left-0 right-0 top-3 h-px bg-zinc-200" aria-hidden />
        <motion.div
          className="absolute left-0 top-3 h-px bg-primary"
          initial={false}
          animate={{ width: `${fill * 100}%` }}
          transition={{ type: 'spring', stiffness: 60, damping: 18 }}
          aria-hidden
        />

        <ol className="relative flex items-start justify-between">
          {SETUP_STAGES.map((id, index) => {
            const def = STAGE_DEFS[id];
            const done = index < current;
            const isCurrent = index === current;

            return (
              <li
                key={id}
                className={cn(
                  'flex flex-col items-center gap-2 text-center',
                  index === 0 && 'items-start text-left',
                  index === lastIndex && 'items-end text-right'
                )}
                style={{ width: `${100 / SETUP_STAGES.length}%` }}
              >
                <span className="relative flex h-6 w-6 items-center justify-center">
                  {isCurrent && (
                    <motion.span
                      className="absolute inset-0 rounded-full border border-primary"
                      animate={{ scale: [1, 1.55, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                      aria-hidden
                    />
                  )}
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full border bg-white transition-colors',
                      done && 'border-primary bg-primary text-white',
                      isCurrent && 'border-primary bg-primary text-white',
                      !done && !isCurrent && 'border-zinc-300 text-zinc-300'
                    )}
                  >
                    {done ? (
                      <CheckMark />
                    ) : isCurrent ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    )}
                  </span>
                </span>

                {/* Labels collide below ~640px — there the caption under the
                    rail carries the same information without the pile-up. */}
                <span
                  className={cn(
                    'hidden font-mono text-[11px] uppercase tracking-eyebrow sm:block',
                    isCurrent ? 'text-primary' : done ? 'text-zinc-500' : 'text-zinc-400'
                  )}
                >
                  {def.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-eyebrow text-primary sm:hidden">
        Step {current + 1} of {SETUP_STAGES.length} · {STAGE_DEFS[stage].label}
      </p>
    </div>
  );
}
